import { createApi, type BaseFetchQueryFnArgs } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { type BaseQueryApi, type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { angularInjectorProvider } from 'Injector';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import {
    type AssetUploadResponse,
    type AssociatedPlaylist,
    type ContentTopic,
    type ContentTopicsResponse,
    type EntityMetadataResponse,
    type ImageUploadResponse,
    type KeyTermDefinitionResponse,
    type LessonKeyTermDefinition,
    type PublishMeta,
    type S3Asset,
    type SaveStreamRecord,
    type StreamEntityMetadata,
    type StreamRecord,
    type StreamShowResponse,
    type StreamUpdateResponse,
} from './StreamEditor.types';

const COLLECTION = 'lesson_streams';

/*
    A stream save runs publish!, a materialized view refresh, tutor bot record updates and version
    pruning inside one transaction. The Angular save passed `timeout: undefined`, so the 30 second
    default of the shared base query is not a safe ceiling.

    It goes on the save alone, not on the slice. As a slice default it also covered the load, and a
    hung getStream then held the spinner for two minutes rather than thirty seconds.
*/
const SAVE_TIMEOUT = 120000;

// The editor works in snake_case from end to end, because it PUTs the record the server sent it.
// See casingRoundTrip.spec.ts for what the shared conversion does to this payload.
const baseConfig = getBackRoyalApiBaseConfig('streamEditorApi', {
    convertCasing: false,
});

// The params the Angular directive sent through Stream.getCachedOrShow. `published: false` asks for
// the working version, which needs the view_unpublished permission.
const SHOW_PARAMS = {
    filters: { published: false, in_users_locale_or_en: null, user_can_see: null },
    'except[]': ['lesson_streams_progress'],
    include_progress: false,
};

type QueryFnBaseQueryResult = { data?: unknown; error?: FetchBaseQueryError; meta?: Record<string, unknown> };
type QueryFnBaseQuery = (args: BaseFetchQueryFnArgs) => QueryFnBaseQueryResult | PromiseLike<QueryFnBaseQueryResult>;

// Appends a file part, reading the bytes rather than passing the picked File straight through.
// Chromium re-checks a File's size and mtime when it reads the request body, and a cloud provider
// that hydrates a placeholder file on first read fails that check with ERR_UPLOAD_FILE_CHANGED. The
// Blob carries no name, so the filename goes in explicitly. Same helper as
// ProjectSubmission/uploadProjectSubmissionApi.
async function appendFile(formData: FormData, key: string, file: File) {
    const blob = new Blob([await file.arrayBuffer()], { type: file.type });
    formData.append(key, blob, file.name);
}

async function buildUploadFormData(key: string, files: File[]): Promise<FormData | FetchBaseQueryError> {
    const formData = new FormData();
    try {
        for (const file of files) {
            await appendFile(formData, key, file);
        }
        return formData;
    } catch (e) {
        const ErrorLogService = angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
        ErrorLogService?.notify('Could not read a course editor upload', e, { file_count: files.length });
        return { status: 'CUSTOM_ERROR', error: e instanceof Error ? e.message : String(e) };
    }
}

async function uploadFiles<Response>(
    { url, key, files }: { url: string; key: string; files: File[] },
    fetchWithBaseQuery: QueryFnBaseQuery,
): Promise<{ data: Response; error?: undefined } | { error: FetchBaseQueryError; data?: undefined }> {
    const formData = await buildUploadFormData(key, files);
    if (!(formData instanceof FormData)) return { error: formData };

    const response = (await fetchWithBaseQuery({ url, method: 'POST', body: formData })) as {
        data?: Response;
        error?: FetchBaseQueryError;
    };

    if (response.error) return { error: response.error };
    return { data: response.data as Response };
}

export function streamFromShowResponse(response: StreamShowResponse): StreamRecord {
    const record = response?.contents?.lesson_streams?.[0];
    if (!record) throw new Error('Unexpected response shape from the course load');
    return record;
}

export const streamEditorApi = createApi('streamEditorApi', {
    ...baseConfig,
    tagTypes: ['Stream', 'AssociatedPlaylists', 'ContentTopics'],
    // oxlint-disable-next-line max-lines-per-function -- one object literal of endpoints
    endpoints: builder => ({
        getStream: builder.query<StreamRecord, string>({
            query: id => ({
                url: `${COLLECTION}/${id}.json`,
                params: SHOW_PARAMS,
            }),
            transformResponse: streamFromShowResponse,
            providesTags: (_result, _error, id) => [{ type: 'Stream', id }],

            // Re-entering the editor must not serve a stale updated_at. A stale one makes the next
            // save fail the optimistic lock check with a 406.
            forceRefetch: () => true,
        }),

        updateStream: builder.mutation<Partial<StreamRecord>, { record: SaveStreamRecord; meta: PublishMeta }>({
            query: ({ record, meta }) => ({
                url: `${COLLECTION}.json`,
                method: 'PUT',
                body: { record, meta },
                timeout: SAVE_TIMEOUT,
            }),
            transformResponse: (response: StreamUpdateResponse) => response?.contents?.lesson_streams?.[0] || {},

            // The response carries UPDATE_FIELDS only, so invalidating the Stream tag would refetch
            // the whole record and discard edits the user has not saved yet. Fold the returned
            // timestamps into the cached record instead.
            async onQueryStarted({ record }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    // The cache holds what was saved, plus the timestamps the server returned. Only
                    // patching the timestamps would leave the cache holding pre-save values, and
                    // the page would then reset its form to them.
                    //
                    // `lessons` is left alone: the payload trims each lesson to its id, and the
                    // cached record carries the whole lesson, which the key terms dialog reads.
                    const { lessons, ...savedFields } = record;

                    dispatch(
                        streamEditorApi.util.updateQueryData('getStream', record.id, draft => {
                            Object.assign(draft, savedFields, data);
                        }),
                    );
                } catch {
                    // The caller reports the failure. Leave the cache as it was.
                }
            },

            // Publishing can change which playlists are valid.
            invalidatesTags: (_result, _error, { record }) => [{ type: 'AssociatedPlaylists', id: record.id }],

            /*
                The shared base query retries once on a disconnect or a 502/504, and RTK applies that
                to mutations too. A retried save re-runs publish! and then fails the updated_at
                check. The Angular $http path never retried.
            */
            extraOptions: { maxRetries: 0 },
        }),

        destroyStream: builder.mutation<void, string>({
            query: id => ({
                url: `${COLLECTION}/${id}.json`,
                method: 'DELETE',
            }),
            extraOptions: { maxRetries: 0 },
        }),

        // A duplicate creates a new course, in a new locale pack or in the same one for a
        // translation. A retry would create a second course.
        duplicateStream: builder.mutation<
            StreamRecord,
            {
                title: string;
                locale: string;
                duplicateFromId: string;
                duplicateFromUpdatedAt: number;
                inSameLocalePack: boolean;
            }
        >({
            query: ({ title, locale, duplicateFromId, duplicateFromUpdatedAt, inSameLocalePack }) => ({
                url: `${COLLECTION}.json`,
                method: 'POST',
                body: {
                    record: { title, locale },
                    meta: {
                        in_same_locale_pack: inSameLocalePack,
                        duplicate_from_id: duplicateFromId,
                        duplicate_from_updated_at: duplicateFromUpdatedAt,
                    },
                },
            }),
            transformResponse: streamFromShowResponse,
            extraOptions: { maxRetries: 0 },
        }),

        // The SEO metadata saves on its own request, before the course. It has no optimistic lock.
        updateEntityMetadata: builder.mutation<StreamEntityMetadata, { record: StreamEntityMetadata }>({
            query: ({ record }) => ({
                url: 'entity_metadata.json',
                method: 'PUT',
                body: { record },
            }),
            transformResponse: (response: EntityMetadataResponse) => {
                const record = response?.contents?.entity_metadata?.[0];
                if (!record) throw new Error('Unexpected response shape from the SEO metadata save');
                return record;
            },
            extraOptions: { maxRetries: 0 },
        }),

        uploadStreamImages: builder.mutation<S3Asset[], { streamId: string; files: File[] }>({
            async queryFn({ streamId, files }, _api: BaseQueryApi, _options, fetchWithBaseQuery: QueryFnBaseQuery) {
                const result = await uploadFiles<ImageUploadResponse>(
                    { url: `${COLLECTION}/${streamId}/images.json`, key: 'images[]', files },
                    fetchWithBaseQuery,
                );
                if (result.error) return { error: result.error };
                return { data: result.data.contents.images };
            },
            extraOptions: { maxRetries: 0 },
        }),

        uploadEntityMetadataImages: builder.mutation<S3Asset[], { entityMetadataId: string; files: File[] }>({
            async queryFn(
                { entityMetadataId, files },
                _api: BaseQueryApi,
                _options,
                fetchWithBaseQuery: QueryFnBaseQuery,
            ) {
                const result = await uploadFiles<ImageUploadResponse>(
                    { url: `entity_metadata/${entityMetadataId}/images.json`, key: 'images[]', files },
                    fetchWithBaseQuery,
                );
                if (result.error) return { error: result.error };
                return { data: result.data.contents.images };
            },
            extraOptions: { maxRetries: 0 },
        }),

        // Resource downloads and summaries upload to the shared asset endpoint.
        uploadAssets: builder.mutation<S3Asset[], { files: File[] }>({
            async queryFn({ files }, _api: BaseQueryApi, _options, fetchWithBaseQuery: QueryFnBaseQuery) {
                const result = await uploadFiles<AssetUploadResponse>(
                    { url: 'assets.json', key: 'files[]', files },
                    fetchWithBaseQuery,
                );
                if (result.error) return { error: result.error };
                return { data: result.data.contents.s3_assets };
            },
            extraOptions: { maxRetries: 0 },
        }),

        // This endpoint renders a bare array rather than the usual contents wrapper.
        getAssociatedPlaylists: builder.query<AssociatedPlaylist[], string>({
            query: id => ({ url: `${COLLECTION}/${id}/associated_playlists` }),
            providesTags: (_result, _error, id) => [{ type: 'AssociatedPlaylists', id }],
        }),

        // One key term definition, edited in the review dialog. The endpoint keys on the raw term,
        // so the display text is no use here.
        saveKeyTermDefinition: builder.mutation<
            LessonKeyTermDefinition,
            { lessonId: string; keyTerm: string; definition: string }
        >({
            query: ({ lessonId, keyTerm, definition }) => ({
                url: 'lesson_key_term_definitions.json',
                method: 'PUT',
                body: { lesson_id: lessonId, key_term: keyTerm, definition },
            }),
            transformResponse: (response: KeyTermDefinitionResponse) => {
                const record = response?.contents?.lesson_key_term_definitions?.[0];
                if (!record) throw new Error('Unexpected response shape from the key term definition save');
                return record;
            },
            extraOptions: { maxRetries: 0 },
        }),

        getContentTopics: builder.query<ContentTopic[], void>({
            query: () => ({ url: 'content_topics.json' }),
            transformResponse: (response: ContentTopicsResponse) => response?.contents?.content_topics || [],
            providesTags: ['ContentTopics'],
        }),
    }),
});
